import React, { useState } from 'react';
import LazyThumbnail from './LazyThumbnail';
import DesignQualityPanel from './DesignQualityPanel';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Sparkles, FileEdit, Image } from 'lucide-react';

const normalizeList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string') return value.trim() ? [value.trim()] : [];
  return [];
};

const uniqueList = (items) => Array.from(new Set((items || []).filter(Boolean)));

const getSlideTitle = (slide, index) => {
  const raw = slide?.text_content || '';
  const lines = raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const titleLine = lines.find((line) => line.toUpperCase().startsWith('SLIDE TITLE:'));
  if (titleLine) {
    return titleLine.replace(/SLIDE TITLE:/i, '').trim() || `Slide ${slide?.slide_number || index + 1}`;
  }
  return lines[0] || `Slide ${slide?.slide_number || index + 1}`;
};

export function DuarteSlideCard({ slide, index, onGenerateVisual, onRewriteContent, onApplyAll, generatedContent, projectId, fileHash, isLargeFile }) {
  const [isGeneratingVisual, setIsGeneratingVisual] = useState(false);
  const [isRewritingContent, setIsRewritingContent] = useState(false);
  const [isApplyingAll, setIsApplyingAll] = useState(false);
  const [showAllContent, setShowAllContent] = useState(false);
  const [showAllVisual, setShowAllVisual] = useState(false);

  const title = getSlideTitle(slide, index);
  const slideNumber = slide?.slide_number || index + 1;

  // DEBUG: Log nano banana preview URL
  if (slideNumber <= 5) {
    console.log(`🍌 Slide ${slideNumber} nano_banana_preview_url:`, slide?.nano_banana_preview_url ? 'EXISTS' : 'MISSING');
    console.log(`🍌 Slide ${slideNumber} keys:`, Object.keys(slide || {}));
  }

  // Extract recommendations from slide analysis - support both structures
  const recommendations = slide?.recommendations || slide?.guidance || {};

  // Handle both old API structure and new recommendations structure
  const newTitle = recommendations?.newTitle ||
    recommendations?.title_recommendation ||
    recommendations?.slideOverview?.slidePurpose ||
    title;

  // Map content refinements from different possible sources
  const contentRefinements = uniqueList([
    ...normalizeList(recommendations?.content_refinements),
    ...normalizeList(recommendations?.contentRefinement),
    ...normalizeList(recommendations?.contentAnalysis?.textContent?.improvements),
    ...normalizeList(recommendations?.designRecommendations?.layout?.specificChanges),
  ]);

  // Map visual content recommendations from different sources
  const visualContent = uniqueList([
    ...normalizeList(recommendations?.visual_content),
    ...normalizeList(recommendations?.visualRecommendations),
    ...normalizeList(recommendations?.contentAnalysis?.visualElements?.images?.recommendations),
    recommendations?.visualSuggestions?.description,
    recommendations?.visualSuggestions?.recommendedImage?.description,
  ]);

  return (
    <Card className="overflow-hidden border-border/40">
      <CardContent className="p-0">
        <div className="grid md:grid-cols-[360px,1fr] gap-0">
          {/* Left: Slide Thumbnail + Design Quality */}
          <div className="bg-muted/20 p-8 space-y-6">
            {slide?.nano_banana_preview_url ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground text-center uppercase tracking-wide">Original Slide</p>
                  <div className="relative flex items-center justify-center">
                    <LazyThumbnail
                      thumbnailUrl={slide?.thumbnail_url}
                      lazyThumbnailUrl={slide?.lazy_thumbnail_url}
                      slideNumber={slideNumber}
                      className="w-full h-auto object-contain rounded-lg shadow-lg border border-border/30"
                      onError={(err) => console.error(`Card thumbnail error for slide ${slideNumber}:`, err)}
                      slide={slide}
                      projectId={projectId}
                      fileHash={fileHash}
                      isLargeFile={isLargeFile}
                    />
                    <Badge
                      variant="secondary"
                      className="absolute bottom-4 left-4 bg-background/90 backdrop-blur-sm"
                    >
                      Slide {slideNumber}
                    </Badge>
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-brand-mint text-center uppercase tracking-wide">Nano Banana Pro Preview</p>
                  <div className="relative flex items-center justify-center">
                    <img
                      src={slide.nano_banana_preview_url}
                      alt="Nano Banana Pro Preview"
                      className="w-full h-auto object-contain rounded-lg shadow-lg border border-brand-mint/30"
                    />
                    <div className="absolute top-2 right-2 bg-brand-navy/80 text-brand-mint p-1.5 rounded-full backdrop-blur-sm">
                      <Sparkles className="w-3 h-3" />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative flex items-center justify-center">
                <LazyThumbnail
                  thumbnailUrl={slide?.thumbnail_url}
                  lazyThumbnailUrl={slide?.lazy_thumbnail_url}
                  slideNumber={slideNumber}
                  className="w-full h-auto object-contain rounded-lg shadow-lg border border-border/30"
                  onError={(err) => console.error(`Card thumbnail error for slide ${slideNumber}:`, err)}
                  slide={slide}
                  projectId={projectId}
                  fileHash={fileHash}
                  isLargeFile={isLargeFile}
                />
                <Badge
                  variant="secondary"
                  className="absolute bottom-4 left-4 bg-background/90 backdrop-blur-sm"
                >
                  Slide {slideNumber}
                </Badge>
              </div>
            )}
          </div>

          {/* Right: Recommendations with Design Quality */}
          <div className="p-8 space-y-8">
            {/* Content Recommendations */}
            <div className="space-y-6">
              {/* New Title Section */}
              <div className="space-y-2">
                <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  New title:
                </p>
                <h3 className="text-xl font-bold text-foreground leading-tight">
                  {newTitle}
                </h3>
              </div>

              {/* Content Refinement */}
              {contentRefinements.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-foreground">
                    Content refinement
                  </h4>
                  <ul className="space-y-2">
                    {(showAllContent ? contentRefinements : contentRefinements.slice(0, 3)).map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-foreground/80">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  {contentRefinements.length > 3 && (
                    <button
                      onClick={() => setShowAllContent(!showAllContent)}
                      className="text-xs text-primary hover:underline font-medium"
                    >
                      {showAllContent ? 'View less' : `View ${contentRefinements.length - 3} more`}
                    </button>
                  )}
                </div>
              )}

              {/* Visual Content */}
              {visualContent.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-foreground">
                    Visual content
                  </h4>
                  <ul className="space-y-2">
                    {(showAllVisual ? visualContent : visualContent.slice(0, 3)).map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-foreground/80">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  {visualContent.length > 3 && (
                    <button
                      onClick={() => setShowAllVisual(!showAllVisual)}
                      className="text-xs text-primary hover:underline font-medium"
                    >
                      {showAllVisual ? 'View less' : `View ${visualContent.length - 3} more`}
                    </button>
                  )}
                </div>
              )}

              {/* Fallback if no recommendations */}
              {contentRefinements.length === 0 && visualContent.length === 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-foreground">
                    Content refinement
                  </h4>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-sm text-foreground/80">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                      <span>Maintain clear and concise messaging</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-foreground/80">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                      <span>Ensure content supports presentation objectives</span>
                    </li>
                  </ul>
                  <h4 className="text-sm font-semibold text-foreground pt-3">
                    Visual content
                  </h4>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-sm text-foreground/80">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                      <span>Maintain visual hierarchy and consistency</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-foreground/80">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                      <span>Use appropriate visual elements to support key messages</span>
                    </li>
                  </ul>
                </div>
              )}
            </div>

            {/* Design Quality Panel - Full Width Below */}
            {slide?.design_quality && (
              <DesignQualityPanel
                designQuality={slide.design_quality}
                slideNumber={slideNumber}
              />
            )}

            {/* Show generated content if available */}
            {generatedContent && (generatedContent.imageUrl || generatedContent.rewritten) && (
              <div className="space-y-4 pt-4 border-t border-border/40">
                <h4 className="text-sm font-semibold text-primary">
                  Generated Content
                </h4>

                {generatedContent.imageUrl && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Generated Visual:</p>
                    <div className="flex items-center justify-center bg-muted/20 rounded-md p-4">
                      <img
                        src={generatedContent.imageUrl}
                        alt="Generated visual"
                        className="w-full max-w-md object-contain rounded"
                      />
                    </div>
                  </div>
                )}

                {generatedContent.rewritten && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Rewritten Content:</p>
                    <div className="bg-muted/30 p-4 rounded-md space-y-2">
                      <p className="text-base font-semibold">{generatedContent.rewritten.title}</p>
                      <p className="text-sm text-foreground/80 whitespace-pre-wrap">{generatedContent.rewritten.body}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col gap-2 pt-4 border-t border-border/40">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                disabled={visualContent.length === 0 || isGeneratingVisual}
                onClick={async () => {
                  if (onGenerateVisual) {
                    setIsGeneratingVisual(true);
                    try {
                      await onGenerateVisual(slide, visualContent);
                    } finally {
                      setIsGeneratingVisual(false);
                    }
                  }
                }}
              >
                <Image className="w-4 h-4 mr-2" />
                {isGeneratingVisual ? 'Generating...' : 'Generate Visual'}
              </Button>

              <Button
                variant="outline"
                size="sm"
                className="w-full"
                disabled={contentRefinements.length === 0 || isRewritingContent}
                onClick={async () => {
                  if (onRewriteContent) {
                    setIsRewritingContent(true);
                    try {
                      await onRewriteContent(slide, contentRefinements, newTitle);
                    } finally {
                      setIsRewritingContent(false);
                    }
                  }
                }}
              >
                <FileEdit className="w-4 h-4 mr-2" />
                {isRewritingContent ? 'Rewriting...' : 'Rewrite Content'}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default DuarteSlideCard;
